/**
 * Add stock, in CenoX: one product or many, without a spreadsheet.
 *
 * Two kinds of stock, kept apart because they mean different things:
 *
 *   Already on the shelf   opening stock: what the pharmacy held when it joined.
 *                          Becomes an opening-stock run — the same rules and
 *                          approval as an uploaded sheet — and is written only
 *                          when a stock approver approves it. An owner or admin
 *                          who approves stock has it written as they send it,
 *                          all lines or none. Medicines entered by anyone but
 *                          a registered pharmacist are written to stock held,
 *                          each batch awaiting a pharmacist's check before sale.
 *   Received from supplier a delivery: a goods receipt against a supplier. If
 *                          whoever receives it cannot approve stock, it is held
 *                          until a pharmacist checks it.
 *
 * Every line is what is printed on the pack. Nothing is filled in: a batch,
 * an expiry or a cost left blank is left blank, and the server holds or
 * refuses the line rather than inventing one.
 *
 * Opened from "Counted stock — batch/expiry pending" with items picked, or
 * from Add stock on a product that still has counted units awaiting batches:
 * each is a line with what is still awaited and the cost it was counted at —
 * the count's own figures — and only the batch, expiry and storage are typed
 * in. Such a line gives the counted units their batch; it adds none. The
 * opening stock it writes draws the count down by the same amount, so the
 * product's recorded stock stays where it was, and the server refuses a line
 * that would go past what the count still awaits.
 *
 * Opened from a product whose stock already has its batches, nothing is
 * filled in: those details are on file, and what is entered here is new stock
 * on top of it, which the page says before anything is sent.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Copy, PackagePlus, Trash2 } from 'lucide-react';
import { STORAGE_REQUIREMENT_LABELS } from '@cenox/shared';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { todayIn } from '../../lib/format';
import { Badge, Button, Card, CardHeader, EmptyState, Input, Select, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { RunView } from './ImportPage';
import { Checkbox, LocationPicker, ProductPicker, useActionBranch } from './MedicinesPage';
import { usePharmacyWrite, type UnitView } from './data';
import { ErrorLine } from './parts';
import type { CountedLine } from './CountedStockCard';

type Mode = 'opening' | 'received';

/** What a line needs to know about its product. */
interface ProductFacts {
  id: string;
  name: string;
  isMedicine: boolean;
  tracksBatches: boolean;
  storageRequirement: string;
  baseUnitName: string;
  units: UnitView[];
  onHand?: number;
}

/** A batch already held, shown when a product's stock already has its details. */
interface HeldBatch {
  batchId: string;
  batchNumber: string;
  expiryDate: string;
  onHand: number;
  branchId: string;
}

/** What a product opened from "Add stock" already holds, said before new stock is added on top. */
interface AlreadyHeld {
  name: string;
  onHand: number;
  batches: HeldBatch[];
}

interface CountedItem {
  productId: string;
  branchId: string;
  awaiting: number;
  unitCost: string;
}

interface Line {
  key: string;
  product: ProductFacts;
  unitId: string;
  quantity: string;
  batchNumber: string;
  expiryDate: string;
  manufacturedOn: string;
  unitCost: string;
  storageConfirmed: boolean;
  /** Filled in from the count: what was still awaiting batch details. */
  counted?: number;
  /** Gives counted units their batch rather than adding stock (and every "Another batch" split from one). */
  completesCount?: boolean;
  /** For such a line: what the count still awaits for this product at the location. */
  countAwaits?: number;
}

const COLD = new Set(['cool', 'refrigerated', 'frozen']);
const isWhole = (v: string) => /^\d+$/.test(v.trim()) && Number(v) > 0;
const isMoney = (v: string) => /^\d+(\.\d{1,2})?$/.test(v.trim());
let counter = 0;
const nextKey = () => `line-${++counter}`;

function useProductFacts() {
  const path = useBusinessPath();
  return (id: string) => api.get<ProductFacts>(path(`/pharmacy/medicines/${id}`));
}

/** What is counted of one product and still awaits batches, and the batches it already holds. */
function useProductStock() {
  const path = useBusinessPath();
  return {
    counted: async (productId: string) =>
      (await api.get<{ data: CountedItem[] }>(path('/pharmacy/counted-stock'))).data.filter(
        (c) => c.productId === productId && c.awaiting > 0,
      ),
    batches: async (productId: string) =>
      (await api.get<{ data: HeldBatch[] }>(path(`/pharmacy/batches?productId=${productId}`))).data,
  };
}

/** A line from the count: its awaiting quantity and counted cost, the batch typed from the pack. */
function countedLine(facts: ProductFacts, c: { awaiting: number; unitCost: string }): Line {
  const base = facts.units.find((u) => u.isBase) ?? facts.units[0];
  return {
    key: nextKey(),
    product: facts,
    unitId: base?.id ?? '',
    quantity: String(c.awaiting),
    batchNumber: '',
    expiryDate: '',
    manufacturedOn: '',
    unitCost: c.unitCost,
    storageConfirmed: false,
    counted: c.awaiting,
    completesCount: true,
    countAwaits: c.awaiting,
  };
}

function useSuppliers() {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['pharmacy', path(''), 'suppliers-core'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string; isActive: boolean }> }>(path('/suppliers')),
  });
}

export function StockEntryPage(): ReactNode {
  const { can, business } = useAuth();
  const mayOpen = can('pharmacy:stock') || can('imports:manage');
  const mayReceive = can('pharmacy:purchasing');
  // Written as sent, not left for approval: the server decides, this only sets expectations.
  const postsNow = (business?.role === 'owner' || business?.role === 'admin') && can('pharmacy:stock_approve');
  const [mode, setMode] = useState<Mode>(mayOpen ? 'opening' : 'received');
  const location = useActionBranch();
  const suppliers = useSuppliers();
  const factsOf = useProductFacts();
  const stockOf = useProductStock();
  const [alreadyHeld, setAlreadyHeld] = useState<AlreadyHeld | null>(null);
  const timezone = business?.timezone ?? 'UTC';
  const today = todayIn(timezone);
  const [supplierId, setSupplierId] = useState('');
  const [receivedOn, setReceivedOn] = useState(today);
  const [lines, setLines] = useState<Line[]>([]);
  const [picking, setPicking] = useState<{ id: string; name: string } | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [posted, setPosted] = useState(false);
  const [heldForPharmacist, setHeldForPharmacist] = useState(false);
  const [received, setReceived] = useState<{ reference: string } | null>(null);
  const [submitKey, setSubmitKey] = useState(() => `stock-${crypto.randomUUID()}`);
  const [params] = useSearchParams();
  const entry = usePharmacyWrite<Record<string, unknown>, { id: string; posted?: boolean; heldForPharmacist?: boolean }>('post', '/stock-entries');
  const receipt = usePharmacyWrite<Record<string, unknown>, { reference: string }>('post', '/receipts');

  async function add(id: string) {
    const facts = await factsOf(id);
    const base = facts.units.find((u) => u.isBase) ?? facts.units[0];
    setLines((all) => [
      ...all,
      {
        key: nextKey(),
        product: facts,
        unitId: base?.id ?? '',
        quantity: '',
        batchNumber: '',
        expiryDate: '',
        manufacturedOn: '',
        unitCost: '',
        storageConfirmed: false,
      },
    ]);
  }

  // Opened from the counted items awaiting batches: one line each, quantity and cost from the count.
  const fromCount = (useLocation().state as { counted?: CountedLine[] } | null)?.counted;
  const loadedCount = useRef(false);
  const [loadingCount, setLoadingCount] = useState(false);
  useEffect(() => {
    if (!fromCount?.length || loadedCount.current) return;
    loadedCount.current = true;
    setMode('opening');
    if (fromCount[0]) location.setBranchId(fromCount[0].branchId);
    setLoadingCount(true);
    void (async () => {
      const filled: Line[] = [];
      for (const c of fromCount) filled.push(countedLine(await factsOf(c.productId), c));
      setLines((all) => [...all, ...filled]);
      setLoadingCount(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCount]);

  /*
    Opened from a product's "Add stock": that product is the first line.

    Counted units still awaiting batches are what that product is missing,
    so the line is the count's — its quantity and cost filled in, giving
    those units their batch without adding any. Otherwise the line is blank:
    stock already held has its batch on file, and copying its quantity in
    would enter it a second time.
  */
  const preselected = params.get('product');
  // Once, even where React runs an effect twice: one product, one line.
  const added = useRef<string | null>(null);
  useEffect(() => {
    if (!preselected || added.current === preselected) return;
    added.current = preselected;
    void (async () => {
      const [facts, counted] = await Promise.all([factsOf(preselected), stockOf.counted(preselected)]);
      const here = counted.find((c) => c.branchId === location.branchId) ?? counted[0];
      if (here && mayOpen) {
        setMode('opening');
        location.setBranchId(here.branchId);
        setLines((all) => [...all, countedLine(facts, here)]);
        return;
      }
      await add(preselected);
      if ((facts.onHand ?? 0) > 0) {
        const batches = facts.tracksBatches ? (await stockOf.batches(preselected)).filter((b) => b.onHand > 0) : [];
        setAlreadyHeld({ name: facts.name, onHand: facts.onHand ?? 0, batches });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselected]);

  /** The counted lines become a blank new-stock line each: for a delivery, not the count. */
  function enterAsNewStock() {
    setLines((all) =>
      all.map((l) =>
        l.completesCount
          ? { ...l, quantity: '', unitCost: '', batchNumber: '', expiryDate: '', manufacturedOn: '', counted: undefined, completesCount: false, countAwaits: undefined }
          : l,
      ),
    );
  }

  const completing = lines.some((l) => l.completesCount);
  // What the count still awaits per product, carried on each of its lines.
  const awaitingOf = new Map<string, number>();
  for (const l of lines) if (l.completesCount && l.countAwaits !== undefined) awaitingOf.set(l.product.id, l.countAwaits);
  const enteredFromCount = new Map<string, number>();
  for (const l of lines) {
    if (l.completesCount && isWhole(l.quantity)) enteredFromCount.set(l.product.id, (enteredFromCount.get(l.product.id) ?? 0) + Number(l.quantity));
  }

  // A product picked is added as a line, and the picker is ready for the next one.
  useEffect(() => {
    if (picking) {
      void add(picking.id);
      setPicking(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picking]);

  const set = (key: string, patch: Partial<Line>) => setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const problems = lines.map((l) => {
    if (!isWhole(l.quantity)) return 'Enter a whole quantity';
    if (l.completesCount) {
      const awaiting = awaitingOf.get(l.product.id) ?? 0;
      if ((enteredFromCount.get(l.product.id) ?? 0) > awaiting) {
        return `Only ${awaiting} counted still await batch details. More than that would add new stock: enter it separately.`;
      }
    }
    if (mode === 'received' || postsNow) {
      if (l.product.tracksBatches && (!l.batchNumber.trim() || !l.expiryDate)) return 'Batch number and expiry are read from the pack';
      if (!isMoney(l.unitCost)) return 'Enter what one unit cost';
    }
    if (l.unitCost.trim() && !isMoney(l.unitCost)) return 'Cost is an amount, e.g. 1250.00';
    if (l.manufacturedOn && l.expiryDate && l.manufacturedOn >= l.expiryDate) return 'Made before it expires';
    if (COLD.has(l.product.storageRequirement) && !l.storageConfirmed) return 'Confirm it is stored as required';
    return null;
  });
  const ready = lines.length > 0 && problems.every((p) => !p) && (mode === 'opening' || Boolean(supplierId)) && Boolean(location.branchId);

  function submit() {
    if (mode === 'opening') {
      entry.mutate(
        {
          branchId: location.branchId,
          lines: lines.map((l) => ({
            productId: l.product.id,
            quantity: Number(l.quantity),
            batchNumber: l.batchNumber.trim() || null,
            expiryDate: l.expiryDate || null,
            manufacturedOn: l.manufacturedOn || null,
            unitCost: l.unitCost.trim() || null,
            supplierId: supplierId || null,
            storageConfirmed: l.storageConfirmed,
            completesCount: l.completesCount === true,
          })),
        },
        {
          onSuccess: (run) => {
            setPosted(run.posted === true);
            setHeldForPharmacist(run.heldForPharmacist === true);
            setRunId(run.id);
          },
        },
      );
      return;
    }
    receipt.mutate(
      {
        supplierId,
        branchId: location.branchId,
        receivedOn,
        post: true,
        // One key per submission: a double-click or a retry receives the delivery once.
        idempotencyKey: submitKey,
        lines: lines.map((l) => ({
          productId: l.product.id,
          unitId: l.unitId,
          quantity: Number(l.quantity),
          unitCost: l.unitCost.trim(),
          batchNumber: l.batchNumber.trim() || null,
          expiryDate: l.expiryDate || null,
          manufacturedOn: l.manufacturedOn || null,
          storageConfirmed: l.storageConfirmed,
        })),
      },
      {
        onSuccess: (r) => {
          setReceived({ reference: r.reference });
          setLines([]);
          setSubmitKey(`stock-${crypto.randomUUID()}`);
        },
      },
    );
  }

  if (!mayOpen && !mayReceive) {
    return (
      <div className="space-y-5">
        <PageHeader title="Add stock" />
        <EmptyState title="Adding stock needs a stock or purchasing role" description="Ask the owner if your job includes counting or receiving stock." />
      </div>
    );
  }

  if (runId) {
    return (
      <div className="space-y-5">
        {posted && heldForPharmacist ? (
          <PageHeader
            title="Added to stock — medicines held for a pharmacist"
            description="Everything is in stock now, under your name. Retail items can be sold; each medicine batch is held until a registered pharmacist checks and releases it (Medicines & Stock → Adjustments → Release checked)."
          />
        ) : posted ? (
          <PageHeader title="Added to stock" description="Written to stock now, under your name, and kept in the audit history" />
        ) : (
          <PageHeader title="Opening stock entered" description="Written to stock only when somebody who approves stock approves it" />
        )}
        <RunView
          runId={runId}
          currency={business?.currencyCode ?? 'NGN'}
          doneLabel="Enter more stock"
          onDone={() => {
            setRunId(null);
            setPosted(false);
            setHeldForPharmacist(false);
            setLines([]);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Link to="/pharmacy/stock" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> Medicines & products
      </Link>
      <PageHeader
        title={completing ? 'Add batch & expiry details' : 'Add stock'}
        description={
          completing
            ? 'For stock already counted: its quantity and cost are filled in from the count. Type the batch number and expiry from the pack.'
            : 'Quantities, batches, expiry and cost, entered here — one product or many. A spreadsheet is optional.'
        }
      />

      {completing && (
        <div role="note" className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-slate-700">
          <p className="font-medium text-slate-900">Adding batch &amp; expiry details to existing stock</p>
          <p className="mt-0.5">
            These units are already counted. Saving gives them their batch and expiry; it adds no units, and each product&apos;s
            recorded stock and value stay as they are. A different quantity or cost here would change the record, so correct it only to
            match the shelf.
          </p>
          <button type="button" className="mt-1 text-brand-700 hover:underline" onClick={enterAsNewStock}>
            Entering a new delivery instead?
          </button>
        </div>
      )}

      {alreadyHeld && !completing && (
        <div role="note" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">
            {alreadyHeld.name} already has {alreadyHeld.onHand} on hand
            {alreadyHeld.batches.length ? ', with its batch details recorded:' : '.'}
          </p>
          {alreadyHeld.batches.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {alreadyHeld.batches.map((b) => (
                <li key={`${b.batchId}-${b.branchId}`}>
                  Batch <span className="font-mono">{b.batchNumber}</span>, expires {b.expiryDate} — {b.onHand} on hand
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1">
            Anything entered below is new stock, added on top of the {alreadyHeld.onHand}. To change a batch number or expiry, use Correct on
            the product&apos;s batches instead.
          </p>
        </div>
      )}

      {received && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Received as {received.reference}. Anything waiting for a pharmacist&apos;s check is listed under Adjustments &amp; quarantine,
          and sells only once it is released.
        </p>
      )}

      <Card>
        <CardHeader title="What are you adding?" />
        <div role="radiogroup" aria-label="Kind of stock" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(
            [
              [
                'opening',
                'Already on the shelf',
                postsNow
                  ? 'Opening stock you held when you joined CenoX. Added to stock as you send it; medicines are held for a registered pharmacist’s check before they sell, unless you are one.'
                  : 'Opening stock you held when you joined CenoX. A stock approver approves it before it can be sold.',
                mayOpen,
              ],
              ['received', 'Received from a supplier', 'A delivery, recorded as a goods receipt against the supplier.', mayReceive && !completing],
            ] as const
          ).map(([value, label, about, allowed]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={mode === value}
              disabled={!allowed}
              onClick={() => setMode(value)}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                mode === value ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500' : 'border-slate-200 hover:bg-slate-50',
              )}
            >
              <p className="font-medium text-slate-900">{label}</p>
              <p className="mt-0.5 text-xs text-slate-500">{about}</p>
            </button>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <LocationPicker value={location.branchId} onChange={location.setBranchId} options={location.options} label="Stock location" />
          <Select
            label={mode === 'received' ? 'Supplier' : 'Supplier (optional)'}
            required={mode === 'received'}
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            placeholder={mode === 'received' ? 'Choose the supplier' : 'Not stated'}
            options={(suppliers.data?.data ?? []).filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name }))}
          />
          {mode === 'received' && (
            <Input label="Received on" type="date" max={today} value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
          )}
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Products"
          subtitle={
            mode === 'opening'
              ? 'Quantities in base units (tablets, bottles). Cost is per base unit. A blank batch, expiry or cost is held for someone to resolve — never filled in.'
              : 'Quantity and cost in the unit you received. Batch number and expiry are read from the pack.'
          }
        />
        <div className="max-w-xl">
          <ProductPicker value={picking} onChange={setPicking} label="Add a product — type a name, or scan its barcode" />
        </div>

        {loadingCount && <p className="mt-4 text-sm text-slate-500">Filling in the counted items…</p>}
        {lines.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No products yet. Add as many as you are entering; a product can have several batches.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {lines.map((l, i) => {
              const cold = COLD.has(l.product.storageRequirement);
              const base = l.product.units.find((u) => u.isBase);
              return (
                <li key={l.key} className="rounded-xl border border-slate-200 p-3" aria-label={`Line ${i + 1}: ${l.product.name}`}>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-slate-900">
                      {l.product.name}
                      {!l.product.tracksBatches && <span className="ml-2"><Badge tone="warning">No batch tracking</Badge></span>}
                      {l.counted !== undefined && <span className="ml-2"><Badge>Counted: {l.counted} awaiting batches</Badge></span>}
                      {cold && <span className="ml-2"><Badge tone="info">{STORAGE_REQUIREMENT_LABELS[l.product.storageRequirement as keyof typeof STORAGE_REQUIREMENT_LABELS] ?? l.product.storageRequirement}</Badge></span>}
                    </p>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Copy className="size-4" />}
                        onClick={() => setLines((all) => [...all.slice(0, i + 1), { ...l, key: nextKey(), quantity: '', batchNumber: '', expiryDate: '', manufacturedOn: '', unitCost: l.completesCount ? l.unitCost : '', storageConfirmed: false, counted: undefined }, ...all.slice(i + 1)])}
                      >
                        Another batch
                      </Button>
                      <Button size="sm" variant="ghost" aria-label={`Remove ${l.product.name}`} icon={<Trash2 className="size-4" />} onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                    <Input label={mode === 'opening' ? `Quantity (${base?.name ?? l.product.baseUnitName}s)` : 'Quantity'} inputMode="numeric" value={l.quantity} onChange={(e) => set(l.key, { quantity: e.target.value })} />
                    {mode === 'received' && (
                      <Select label="Unit" value={l.unitId} onChange={(e) => set(l.key, { unitId: e.target.value })} options={l.product.units.filter((u) => u.purchasable || u.isBase).map((u) => ({ value: u.id, label: `${u.name} (${u.baseQuantity})` }))} />
                    )}
                    {l.product.tracksBatches && (
                      <>
                        <Input label="Batch number" value={l.batchNumber} onChange={(e) => set(l.key, { batchNumber: e.target.value })} />
                        <Input label="Expiry date" type="date" value={l.expiryDate} onChange={(e) => set(l.key, { expiryDate: e.target.value })} />
                      </>
                    )}
                    <Input label="Made on (optional)" type="date" max={today} value={l.manufacturedOn} onChange={(e) => set(l.key, { manufacturedOn: e.target.value })} />
                    <Input
                      label={mode === 'opening' ? 'Cost per base unit' : 'Cost per unit'}
                      inputMode="decimal"
                      value={l.unitCost}
                      onChange={(e) => set(l.key, { unitCost: e.target.value })}
                    />
                  </div>
                  {cold && (
                    <div className="mt-2">
                      <Checkbox
                        label={`This stock is kept ${(STORAGE_REQUIREMENT_LABELS[l.product.storageRequirement as keyof typeof STORAGE_REQUIREMENT_LABELS] ?? l.product.storageRequirement).toLowerCase()}`}
                        checked={l.storageConfirmed}
                        onChange={(v) => set(l.key, { storageConfirmed: v })}
                      />
                    </div>
                  )}
                  {problems[i] && <p className="mt-2 text-xs font-medium text-amber-700">{problems[i]}</p>}
                  {mode === 'opening' && l.expiryDate && l.expiryDate <= today && (
                    <p className="mt-2 text-xs font-medium text-rose-700">Expired: it is counted and quarantined on arrival, and never sold.</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <ErrorLine error={mode === 'opening' ? entry.error : receipt.error} />
      <div className="flex flex-wrap items-center gap-3">
        <Button icon={<PackagePlus className="size-4" />} disabled={!ready} loading={entry.isPending || receipt.isPending} onClick={submit}>
          {mode === 'opening'
            ? postsNow
              ? `Add ${lines.length || ''} line(s) to stock`
              : `Send ${lines.length || ''} line(s) for approval`
            : `Receive ${lines.length || ''} line(s)`}
        </Button>
        {mode === 'opening' && postsNow && lines.some((l) => l.product.isMedicine) && (
          <p className="w-full text-xs text-slate-600">
            Medicines are added to stock now. Unless your pharmacist registration is on file, each batch is held until a registered pharmacist checks and releases it for sale.
          </p>
        )}
        <Link to="/pharmacy/settings/import" className="text-sm text-brand-700 hover:underline">
          Or import from a spreadsheet
        </Link>
      </div>
    </div>
  );
}
